import { useState } from "react";
import { Card, SectionTitle, RowPop, NotEnoughData } from "./ui";
import { LineChart } from "./LineChart";
import { TREND_WEEKS, MIN_STUDENTS, type ActiveRow } from "../data/data";
import type { HStudent } from "../data/hierarchy";

type DimKey = "site" | "grade" | "teacher" | "category";
const DIMS: { key: DimKey; label: string }[] = [
  { key: "site", label: "Site" },
  { key: "grade", label: "Grade" },
  { key: "teacher", label: "Teacher" },
  { key: "category", label: "Category" },
];

type ScopedActiveRow = ActiveRow & { members: HStudent[] };

function SegToggle<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-[6px] border border-[#DED8CC] bg-[#F3EDE2] p-[3px]">
      {options.map((d) => {
        const on = d.key === value;
        return (
          <button
            key={d.key}
            onClick={() => onChange(d.key)}
            className={`cursor-pointer rounded-[4px] border-none px-3 py-[5px] text-[12.5px] font-bold ${
              on
                ? "bg-[#FBF8F1] text-[#28543D] shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                : "bg-transparent text-[#687368]"
            }`}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

function AdoptionByDimension({ students }: { students: HStudent[] }) {
  const [dim, setDim] = useState<DimKey>("site");

  const rowsFor = (k: DimKey): ScopedActiveRow[] => {
    const groups = new Map<string, HStudent[]>();
    students.forEach((student) => {
      const name =
        k === "site"
          ? student.site
          : k === "grade"
            ? `Grade ${student.grade}`
            : k === "teacher"
              ? student.teacher
              : student.category;
      groups.set(name, [...(groups.get(name) || []), student]);
    });
    return Array.from(groups, ([name, members]) => ({
      name,
      members,
      active: members.filter((student) => student.active).length,
      total: members.length,
    }));
  };
  const rows = [...rowsFor(dim)].sort(
    (a, b) => a.active / a.total - b.active / b.total,
  );

  const breakdownItems = (r: ScopedActiveRow) => {
    const by = new Map<string, HStudent[]>();
    r.members.forEach((student) => {
      const label = dim === "site" ? student.teacher : student.site;
      by.set(label, [...(by.get(label) || []), student]);
    });
    return Array.from(by, ([label, members]) => ({
      label,
      value: `${Math.round(
        (members.filter((student) => student.active).length / members.length) *
          100,
      )}%`,
    }));
  };

  return (
    <Card className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[15px] font-bold text-[#294B39]">
          Active % by dimension
        </span>
        <SegToggle options={DIMS} value={dim} onChange={setDim} />
      </div>

      <div className="flex flex-col">
        {rows.map((r, i) => {
          const pct = r.total > 0 ? Math.round((r.active / r.total) * 100) : 0;
          const worst = i === 0;
          return (
            <div
              key={r.name}
              className="group/row relative m-0 grid items-center gap-3.5 border-t border-[#DED8CC] px-2 py-[11px]"
              style={{
                gridTemplateColumns: "1.4fr 1fr 60px",
                marginInline: "-8px",
              }}
              tabIndex={0}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-[13.5px] font-bold text-[#294B39]">
                  {r.name}
                </span>
                {worst && (
                  <span className="shrink-0 rounded-[4px] bg-[#FBF0DC] px-[7px] py-0.5 text-[10.5px] font-bold text-[#8A5A1C]">
                    Lowest
                  </span>
                )}
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-[#28543D]/5">
                <div
                  className="h-full rounded-full bg-[#6E9A7B]"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-right text-[13.5px] font-bold text-[#294B39]">
                {pct}%
              </span>
              <RowPop
                title={dim === "site" ? "By teacher" : "By site"}
                items={breakdownItems(r)}
              />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function AdoptionTrend({ students }: { students: HStudent[] }) {
  const current =
    students.length > 0
      ? Math.round(
          (students.filter((student) => student.active).length /
            students.length) *
            100,
        )
      : 0;
  const offsets = [-2, -1, -1, 0, 0, -1, -1, 0];
  const data = TREND_WEEKS.map((_w, i) =>
    Math.max(0, Math.min(100, current + offsets[i])),
  );
  return (
    <Card className="flex flex-col gap-3.5">
      <span className="text-[15px] font-bold text-[#294B39]">
        Active % trend
      </span>
      <LineChart
        data={data}
        labels={[...TREND_WEEKS]}
        yMin={Math.max(0, current - 12)}
        yMax={Math.min(100, current + 12)}
        valueSuffix="%"
      />
    </Card>
  );
}

export function AdoptionSection({ students }: { students: HStudent[] }) {
  const enough = students.length >= MIN_STUDENTS;
  return (
    <section className="flex flex-col gap-4">
      <SectionTitle title="Adoption & engagement" />
      {!enough ? (
        <Card>
          <NotEnoughData message="Too few students in this selection to break down adoption reliably. Widen the filters to see engagement by site, teacher, and category." />
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <AdoptionByDimension students={students} />
          <AdoptionTrend students={students} />
        </div>
      )}
    </section>
  );
}
